import { z } from 'zod';
import { idSchema } from './common';

/**
 * Profile kontekstu i pamięć AI (docs/3VTOS_MVP.md §11): `context_profiles`, `memory_proposals`
 * (packages/db/src/schema/context.ts).
 *
 * Profile OWNER (styl komunikacji i zasady decyzji) i BUSINESS (oferta i procesy Fravity) są wersjonowane:
 * każda zmiana = nowy wiersz (`version_no` + 1), aktualna = najwyższa wersja. Edytuje właściciel (owner/admin).
 * „Zapamiętaj” tworzy `memory_proposals` (diff + źródło) → akceptacja właściciela (Approval) → nowa wersja profilu.
 * Profil NIE zawiera danych prywatnych (zdrowie, rodzina, inwestycje) i nie dziedziczy pamięci ChatGPT ani Claude.
 * Wejścia i wyjścia usług dopisuje agent modułu context.
 */

export const CONTEXT_PROFILE_KINDS = ['owner', 'business'] as const;
export type ContextProfileKind = (typeof CONTEXT_PROFILE_KINDS)[number];
export const contextProfileKindSchema = z.enum(CONTEXT_PROFILE_KINDS);

/** Nazwa pliku w widoku Wiedzy i eksporcie (Figma 10: „OWNER.md · v4”, „BUSINESS.md · v2”). */
export const CONTEXT_PROFILE_FILES: Record<ContextProfileKind, string> = {
  owner: 'OWNER.md',
  business: 'BUSINESS.md',
};

export const MEMORY_PROPOSAL_STATUSES = ['proposed', 'accepted', 'rejected'] as const;
export type MemoryProposalStatus = (typeof MEMORY_PROPOSAL_STATUSES)[number];
export const memoryProposalStatusSchema = z.enum(MEMORY_PROPOSAL_STATUSES);

/**
 * Skąd propozycja: polecenie „zapamiętaj”, poprawka maila, spotkanie, wiadomość, zadanie AI, ręcznie; Fala 7: przebieg
 * agenta i zadanie (źródło z MCP `propose_memory_update`, wcześniej zapisywane jako „zapamiętaj” z obcym id).
 */
export const MEMORY_SOURCE_TYPES = [
  'remember_command',
  'email_edit',
  'meeting',
  'message',
  'ai_job',
  'agent_run',
  'task',
  'manual',
] as const;
export type MemorySourceType = (typeof MEMORY_SOURCE_TYPES)[number];

export const memorySourceSchema = z.object({
  type: z.enum(MEMORY_SOURCE_TYPES),
  /** rekord źródła (wiadomość, spotkanie, zadanie AI…), jeśli jest — w bieżącym workspace i w zakresie osoby */
  id: idSchema.nullable().optional(),
  /** krótki cytat, który uzasadnia zmianę (bez danych innych klientów) */
  excerpt: z.string().max(2000).nullable().optional(),
});
export type MemorySource = z.infer<typeof memorySourceSchema>;

/** Treść profilu (Markdown), do 50 tys. znaków: Context Builder wybiera z niej minimum. */
export const contextProfileBodySchema = z
  .string()
  .trim()
  .min(1, { message: 'Profil nie może być pusty' })
  .max(50_000, { message: 'Profil jest za długi' });

export const contextProfileFieldsSchema = z.object({
  kind: contextProfileKindSchema,
  bodyMd: contextProfileBodySchema,
  changeNote: z.string().trim().max(500).nullable().optional(),
});
export type ContextProfileFields = z.infer<typeof contextProfileFieldsSchema>;

export const memoryProposalFieldsSchema = z.object({
  profileKind: contextProfileKindSchema,
  /** wersja profilu, wobec której liczono diff (zmiana profilu w międzyczasie = CONFLICT przy akceptacji) */
  baseVersion: z.number().int().positive(),
  diffMd: z.string().max(20_000),
  proposedBodyMd: contextProfileBodySchema,
  source: memorySourceSchema,
});
export type MemoryProposalFields = z.infer<typeof memoryProposalFieldsSchema>;

/** Kategorie, których nie zapisujemy w profilach (MVP §11). Kontrola treści po stronie usługi i promptu AI. */
export const CONTEXT_PRIVATE_DATA_NOTE =
  'Profil firmy nie zawiera danych prywatnych: zdrowia, rodziny ani inwestycji właściciela.';

// ── Wejścia usług (moduł context, router `context`) ─────────────────────────
export const contextProfileKindInput = z.object({ kind: contextProfileKindSchema }).strict();

export const contextProfileVersionInput = z
  .object({ kind: contextProfileKindSchema, versionNo: z.number().int().positive() })
  .strict();

/**
 * Zapis profilu = nowa wersja. `baseVersion` = wersja, którą osoba edytowała (0 = profil jeszcze nie istnieje);
 * inna niż bieżąca = CONFLICT (ktoś zapisał w międzyczasie).
 */
export const saveContextProfileInput = z
  .object({
    kind: contextProfileKindSchema,
    bodyMd: contextProfileBodySchema,
    changeNote: z.string().trim().max(500).nullable().optional(),
    baseVersion: z.number().int().nonnegative(),
  })
  .strict();
export type SaveContextProfileInput = z.input<typeof saveContextProfileInput>;

export const listMemoryProposalsInput = z
  .object({
    status: memoryProposalStatusSchema.optional(),
    limit: z.number().int().min(1).max(200).default(50),
  })
  .strict()
  .default({});

/**
 * Propozycja zmiany pamięci („zapamiętaj”, AI, MCP): pełna proponowana treść + źródło; diff liczy usługa
 * względem bieżącej wersji (`baseVersion` domyślnie = bieżąca). Z AI i MCP tworzy akceptację `context.memory_update`.
 */
export const proposeMemoryInput = z
  .object({
    profileKind: contextProfileKindSchema,
    proposedBodyMd: contextProfileBodySchema,
    source: memorySourceSchema,
    baseVersion: z.number().int().positive().optional(),
  })
  .strict();
export type ProposeMemoryInput = z.input<typeof proposeMemoryInput>;

export const decideMemoryProposalInput = z
  .object({ id: idSchema, note: z.string().trim().max(500).nullable().optional() })
  .strict();

/** Typ akceptacji: zastosowanie propozycji pamięci (nowa wersja profilu). Zatwierdza właściciel / admin. */
export const MEMORY_UPDATE_APPROVAL = 'context.memory_update';

export const memoryUpdatePayloadSchema = z
  .object({
    proposalId: idSchema,
    profileKind: contextProfileKindSchema,
    /** null = profil nie miał jeszcze wersji */
    baseVersion: z.number().int().positive().nullable(),
    /** krótki opis zmiany do listy akceptacji */
    summary: z.string().max(300),
  })
  .strict();
export type MemoryUpdatePayload = z.infer<typeof memoryUpdatePayloadSchema>;

export const CONTEXT_MESSAGES = {
  editOwnerOnly: 'Profile OWNER i BUSINESS edytuje właściciel albo administrator.',
  readForbidden: 'Profile kontekstu widzą właściciel, administratorzy i managerowie.',
  stale: 'Profil zmienił się w międzyczasie. Odśwież i spróbuj ponownie.',
  proposalStale: 'Profil zmienił się od czasu propozycji. Odrzuć ją albo poproś o nową.',
  alreadyDecided: 'Ta propozycja jest już rozpatrzona.',
  noChange: 'Treść jest taka sama jak w bieżącej wersji.',
  missingProfile: 'Profil nie ma jeszcze żadnej wersji.',
} as const;

// ── DTO ─────────────────────────────────────────────────────────────────────
export interface ContextPersonRef {
  userId: string;
  name: string;
  initials: string;
  avatarColor: string | null;
}

export interface ContextProfileDto {
  kind: ContextProfileKind;
  /** „OWNER.md”, „BUSINESS.md” */
  file: string;
  /** 0 = brak wersji (pusty profil) */
  versionNo: number;
  bodyMd: string;
  author: ContextPersonRef | null;
  createdAt: Date | null;
  changeNote: string | null;
  canEdit: boolean;
}

export interface ContextOverview {
  profiles: ContextProfileDto[];
  /** propozycje czekające na decyzję */
  pendingProposals: number;
  canEdit: boolean;
}

export interface ContextProfileVersionItem {
  versionNo: number;
  author: ContextPersonRef | null;
  createdAt: Date;
  changeNote: string | null;
  current: boolean;
  /** liczba dodanych i usuniętych linii względem poprzedniej wersji */
  added: number;
  removed: number;
}

export interface ContextProfileVersionDto {
  kind: ContextProfileKind;
  versionNo: number;
  bodyMd: string;
  author: ContextPersonRef | null;
  createdAt: Date;
  changeNote: string | null;
  current: boolean;
  /** diff względem poprzedniej wersji (wersja 1: wszystko dodane) */
  diff: DiffLine[];
}

export interface MemoryProposalDto {
  id: string;
  profileKind: ContextProfileKind;
  file: string;
  baseVersion: number | null;
  /** bieżąca wersja profilu (różna od bazowej = propozycja nieaktualna) */
  currentVersion: number;
  stale: boolean;
  diffMd: string;
  diff: DiffLine[];
  proposedBodyMd: string;
  source: MemorySource;
  status: MemoryProposalStatus;
  approvalId: string | null;
  resultingVersion: number | null;
  createdBy: ContextPersonRef | null;
  createdAt: Date;
  decidedBy: ContextPersonRef | null;
  decidedAt: Date | null;
  canDecide: boolean;
}

// ── Diff linii (historia profili, propozycje pamięci) ───────────────────────
export type DiffOp = 'same' | 'add' | 'del';

export interface DiffLine {
  op: DiffOp;
  text: string;
}

/** Limit komórek tablicy LCS po przycięciu wspólnego początku i końca (większe = zastąpienie całości). */
const DIFF_MAX_CELLS = 4_000_000;

function splitLines(s: string): string[] {
  if (s === '') return [];
  return s.replace(/\r\n?/g, '\n').split('\n');
}

/**
 * Diff linia po linii (najdłuższy wspólny podciąg). Działa w przeglądarce i na serwerze, bez zależności.
 * Wspólny początek i koniec są przycinane, więc typowa mała zmiana dużego profilu jest tania.
 */
export function diffLines(before: string, after: string): DiffLine[] {
  const a = splitLines(before);
  const b = splitLines(after);
  let start = 0;
  while (start < a.length && start < b.length && a[start] === b[start]) start++;
  let endA = a.length;
  let endB = b.length;
  while (endA > start && endB > start && a[endA - 1] === b[endB - 1]) {
    endA--;
    endB--;
  }
  const head: DiffLine[] = a.slice(0, start).map((text) => ({ op: 'same', text }));
  const tail: DiffLine[] = a.slice(endA).map((text) => ({ op: 'same', text }));
  const midA = a.slice(start, endA);
  const midB = b.slice(start, endB);
  const n = midA.length;
  const m = midB.length;
  const mid: DiffLine[] = [];
  if (n === 0 || m === 0 || (n + 1) * (m + 1) > DIFF_MAX_CELLS) {
    for (const text of midA) mid.push({ op: 'del', text });
    for (const text of midB) mid.push({ op: 'add', text });
    return [...head, ...mid, ...tail];
  }
  // lcs[i][j] = długość LCS midA[i..] i midB[j..]
  const width = m + 1;
  const lcs = new Uint32Array((n + 1) * width);
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      lcs[i * width + j] =
        midA[i] === midB[j]
          ? (lcs[(i + 1) * width + j + 1] as number) + 1
          : Math.max(lcs[(i + 1) * width + j] as number, lcs[i * width + j + 1] as number);
    }
  }
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (midA[i] === midB[j]) {
      mid.push({ op: 'same', text: midA[i] as string });
      i++;
      j++;
    } else if ((lcs[(i + 1) * width + j] as number) >= (lcs[i * width + j + 1] as number)) {
      mid.push({ op: 'del', text: midA[i] as string });
      i++;
    } else {
      mid.push({ op: 'add', text: midB[j] as string });
      j++;
    }
  }
  while (i < n) mid.push({ op: 'del', text: midA[i++] as string });
  while (j < m) mid.push({ op: 'add', text: midB[j++] as string });
  return [...head, ...mid, ...tail];
}

export function diffStats(lines: readonly DiffLine[]): { added: number; removed: number } {
  let added = 0;
  let removed = 0;
  for (const l of lines) {
    if (l.op === 'add') added++;
    else if (l.op === 'del') removed++;
  }
  return { added, removed };
}

/**
 * Diff w formie tekstu (`memory_proposals.diff_md`): zmienione linie z prefiksem „+ ” / „- ” i do `context`
 * linii kontekstu wokół; przerwy oznaczone „…”.
 */
export function formatDiff(lines: readonly DiffLine[], context = 1): string {
  const keep = new Array<boolean>(lines.length).fill(false);
  lines.forEach((l, idx) => {
    if (l.op === 'same') return;
    for (let k = Math.max(0, idx - context); k <= Math.min(lines.length - 1, idx + context); k++) keep[k] = true;
  });
  const out: string[] = [];
  let skipped = false;
  lines.forEach((l, idx) => {
    if (!keep[idx]) {
      skipped = true;
      return;
    }
    if (skipped && out.length > 0) out.push('…');
    skipped = false;
    out.push(`${l.op === 'add' ? '+ ' : l.op === 'del' ? '- ' : '  '}${l.text}`);
  });
  return out.join('\n');
}
